import { PriorityChange } from './../model/priorityChange';
import { Reassign } from './../model/reassign';
import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subscription, forkJoin } from 'rxjs';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { CaseProcess } from '../../case/model/case';
import { CaseTask } from '../../case/model/case';
import { UserService } from './user.service';
import { WorkflowStatusUpdate } from '../model/workflow-status-update';
import { SensitivitiesUpdate } from '../model/sensitivities-update';
import { MessengerService } from './messenger.service';
import { TranslateService } from '@ngx-translate/core';
import { sortBy } from 'lodash';

/**
 * Provides data services of Cases.
 */
@Injectable({
  providedIn: 'root'
})
export class CaseService implements OnDestroy
{
  public caseManagerAssignmentModes: Array<{ text: string; value: string }> = [
    { text: 'Next Available', value: 'NEXT_AVAILABLE' },
    { text: 'Referrer', value: 'REFERRER' },
    { text: 'Next Available (Receiving Organization Only)', value: 'NEXT_AVAILABLE_RECEIVING_ORG' },
  ];

  private messagingLanguageChangedSubscription: Subscription;

  constructor(
    private http: HttpClient,
    private dictionaryService: DictionaryService,
    private userService: UserService,
    private messengerService: MessengerService,
    private translate: TranslateService,
  )
  {
    this.loadLocalizations();
    this.messagingLanguageChangedSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'languageChanged')
        {
          this.loadLocalizations();
        }
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
  }

  /**
   * Get cases assigned to a user.
   */
  public getCasesByMember(memberId: number): Observable<CaseTask[]>
  {
    return this.http.get<CaseTask[]>(`/cases/member/${memberId}`);
  }

  /**
   * Get the details of a Case.
   *
   * @param caseId Case ID
   */
  public getCaseDetails(caseId: number, taskFilter: string, directFetch: boolean = false): Observable<any>
  {
    const directFetchQueryString = directFetch ? `&directFetch=true` : '';
    return this.http.get<any>(`/cases/${caseId}?filter=${taskFilter}${directFetchQueryString}`);
  }

  /**
   * Get the details of a Case (minimal).
   *
   * @param caseId Case ID
   */
  public getCaseDetailsTasksMinimal(caseId: number, taskFilter: string): Observable<any>
  {
    return this.http.get<any>(`/cases/${caseId}/tasks/minimal?filter=${taskFilter}`);
  }

  /**
   * Creates new case.
   */
  public createCase(aCase: any): Observable<any>
  {
    return this.http.post(`/cases`, aCase);
  }

  /**
   * Updates the case manager of a case.
   *
   * @param caseId The Case ID.
   * @param data The Reassign payload object.
   */
  public updateCaseManager(caseId: number, data: Reassign): Observable<any>
  {
    return this.http.put(`/cases/${caseId}/manager`, data);
  }

  /**
   * Updates the case manager of a case.
   *
   * @param caseId The Case ID.
   * @param data The  payload object.
   */
  public updateCasePriority(caseId: number, data: PriorityChange): Observable<any>
  {
    return this.http.put(`/cases/${caseId}/priority`, data);
  }

  /**
   * Changes the status of a case to "Closed".
   *
   * @param caseId Case ID.
   * @param reason The reason for closing the case.
   */
  public closeCase(caseId: number, reason: string, newWorkflowStatusId: number = null, channelId: number = null): Observable<any>
  {
    const closeCase: any = {};
    closeCase.reason = reason;
    closeCase.channelCategoryWorkflowStatuses = [];
    if (newWorkflowStatusId && newWorkflowStatusId > 0)
    {
      const newStatus: any = {};
      newStatus.channelId = channelId;
      newStatus.channelWorkflowStatusId = newWorkflowStatusId;
      closeCase.channelCategoryWorkflowStatuses.push(newStatus);
    }
    return this.http.put(`/cases/${caseId}/close`, closeCase);
  }

  /**
   * Close a list of cases by ids.
   *
   * @param casesToClose Cases ids to close and reason for closing.
   */
  public closeCases(casesToClose: any): Observable<any>
  {
    return this.http.post(`/cases/close`, casesToClose);
  }

  /**
   * Reassign a list of cases by ids.
   *
   * @param casesToReassign Cases ids to reassign and reason for closing.
   */
  public reassignCases(casesToReassign: any): Observable<any>
  {
    return this.http.post(`/cases/reassign`, casesToReassign);
  }

  /**
   * Get cases of a channel.
   */
  public getCasesByChannel(channelId: number): Observable<CaseProcess[]>
  {
    return this.http.get<any>(`/cases/channel/${channelId}`);
  }

  /**
   * Get all data being used for creating a test member
   */
  public getMemberDataFromMultipleSources(channelId: number): Observable<any[]>
  {
    const genders = this.dictionaryService.getCachedGenders();
    const states = this.dictionaryService.getCachedStatesOrProvinces();
    const countries = this.dictionaryService.getCachedCountries();
    const managers = this.userService.getUsersByChannelGroups(channelId);
    return forkJoin([genders, states, countries, managers]);
  }

  /**
   * Get case performance by user
   */
  public getPerformanceDashboard(): Observable<any[]>
  {
    return this.http.get<any[]>(`/cases/dashboard/performance`);
  }

  /**
   * Get cases assigned to a manager.
   */
  public getCasesByManager(): Observable<CaseTask[]>
  {
    return this.http.get<CaseTask[]>('/cases/manager');
  }

  /**
   * Get an array of CaseTask
   */
  public getCasesTasksByMember(memberId: number): Observable<any[]>
  {
    return this.http.get<any>(`/cases/member/${memberId}/tasks`);
  }

  /**
   * Get Cases from REST service.
   */
  public getCaseCountTabs(caseId: number): Observable<any>
  {
    return this.http.get<any>(`/cases/tabs/count/${caseId}`);
  }

  /**
   * Update the workflow status for a given case id.
   *
   * @param data  New Workflow Status object
   * @param caseId CaseId to update.
   */
  public updateWorkflowStatus(data: WorkflowStatusUpdate, caseId: number): Observable<any>
  {
    return this.http.put(`/cases/${caseId}/workflow/status`, data);
  }

  /**
   * Update the case sensitivities for a given case id.
   *
   * @param data  New case sensitivities object
   * @param caseId CaseId to update.
   */
  public updateSensitivities(data: SensitivitiesUpdate, caseId: number): Observable<any>
  {
    return this.http.put(`/cases/${caseId}/sensitivities`, data);
  }

  public addCaseExtension(addExtension: any, caseId: number): Observable<any>
  {
    return this.http.post(`/cases/${caseId}/extension`, addExtension);
  }

  public deleteCaseExtension(id: number): Observable<any>
  {
    return this.http.delete(`/cases/extension/${id}`);
  }

  public updateCaseExtension(data: any, id?: number): Observable<any>
  {
    return this.http.put(`/cases/extension/${id}`, data);
  }

  public getBusinessProcess(caseId: number): Observable<any>
  {
    return this.http.get<any[]>(`/cases/${caseId}/business-process`);
  }

  /**
   * Save Case recent viewed
   *
   * @param caseId Case Id
   */
  public saveRecentView(caseId: number): Observable<any>
  {
    return this.http.post(`/cases/recent/${caseId}`, null);
  }

  /**
   * Clear all Cases recent viewed
   */
  public clearRecentView(): Observable<any>
  {
    return this.http.post(`/cases/recent/clear`, null);
  }

  public getAssigmentModeText(mode: string): string
  {
    let text = mode;
    const modeObject = this.caseManagerAssignmentModes.find(x => x.value === mode);
    if (modeObject !== undefined)
    {
      text = modeObject.text;
    }
    return text;
  }

  private loadLocalizations(): void
  {
    this.translate.get(['CASE-MANAGER-ASSIGNMENT-MODES']).subscribe({
      next: (res) =>
      {
        const splitted = res['CASE-MANAGER-ASSIGNMENT-MODES'].split(',');
        for (let i = 0; i < splitted.length; i++)
        {
          if (this.caseManagerAssignmentModes.length > i)
          {
            this.caseManagerAssignmentModes[i].text = splitted[i];
          }
        }
        this.caseManagerAssignmentModes = sortBy(this.caseManagerAssignmentModes, ['text']);
      }
    });
  }
}
